import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../core/dialog';
import { Button } from '../core/button';
import { StarRating } from './star-rating';
import { TextArea } from '../core/textarea';

export type FeedbackQuestion = {
  questionId: string;
  questionText: string;
  questionType: 'rating' | 'text';
  order: number;
};

export type FeedbackDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | null;
  questions: FeedbackQuestion[];
  campaignId: string;
  eventId?: string | null;
  isMandatory?: boolean;
  onSubmit: (data: {
    campaignId: string;
    eventId?: string;
    answers: { questionId: string; rating?: number; textAnswer?: string }[];
  }) => void;
  onDismiss: (data: { campaignId: string; eventId?: string }) => void;
  isSubmitting?: boolean;
  labels?: {
    submit?: string;
    dismiss?: string;
    submitting?: string;
    textPlaceholder?: string;
  };
};

export function FeedbackDialog({
  open,
  onOpenChange,
  title,
  description,
  questions,
  campaignId,
  eventId,
  isMandatory = false,
  onSubmit,
  onDismiss,
  isSubmitting = false,
  labels,
}: FeedbackDialogProps) {
  const [ratings, setRatings] = React.useState<Record<string, number>>({});
  const [textAnswers, setTextAnswers] = React.useState<Record<string, string>>({});

  const allAnswered = questions.every((q) => {
    if (q.questionType === 'rating') {
      return ratings[q.questionId] && ratings[q.questionId] > 0;
    }
    return textAnswers[q.questionId]?.trim().length > 0;
  });

  const handleSubmit = () => {
    if (!allAnswered) return;
    justSubmittedRef.current = true;
    onSubmit({
      campaignId,
      eventId: eventId ?? undefined,
      answers: questions.map((q) => ({
        questionId: q.questionId,
        ...(q.questionType === 'rating'
          ? { rating: ratings[q.questionId] }
          : { textAnswer: textAnswers[q.questionId] }),
      })),
    });
  };

  const handleDismiss = () => {
    onDismiss({
      campaignId,
      eventId: eventId ?? undefined,
    });
  };

  const justSubmittedRef = React.useRef(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !justSubmittedRef.current) {
      if (isMandatory) return; // Block closing for mandatory campaigns
      handleDismiss();
      return;
    }
    justSubmittedRef.current = false;
    onOpenChange(newOpen);
  };

  // Reset state when dialog opens with new campaign
  React.useEffect(() => {
    if (open) {
      setRatings({});
      setTextAnswers({});
      justSubmittedRef.current = false;
    }
  }, [open, campaignId]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-md"
        {...(isMandatory ? { onPointerDownOutside: (e: Event) => e.preventDefault(), onEscapeKeyDown: (e: Event) => e.preventDefault() } : {})}
        hideCloseButton={isMandatory}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-6 py-4">
          {questions
            .sort((a, b) => a.order - b.order)
            .map((question) => (
              <div key={question.questionId} className="space-y-2">
                <p className="text-sm font-medium leading-snug">{question.questionText}</p>
                {question.questionType === 'rating' ? (
                  <StarRating
                    value={ratings[question.questionId] ?? 0}
                    onChange={(value) =>
                      setRatings((prev) => ({ ...prev, [question.questionId]: value }))
                    }
                    disabled={isSubmitting}
                  />
                ) : (
                  <TextArea
                    value={textAnswers[question.questionId] ?? ''}
                    onChange={(e) =>
                      setTextAnswers((prev) => ({ ...prev, [question.questionId]: e.target.value }))
                    }
                    placeholder={labels?.textPlaceholder ?? 'Type your answer...'}
                    disabled={isSubmitting}
                    rows={3}
                  />
                )}
              </div>
            ))}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {!isMandatory && (
            <Button
              type="button"
              variant="ghost"
              onClick={handleDismiss}
              disabled={isSubmitting}
            >
              {labels?.dismiss ?? 'Not now'}
            </Button>
          )}
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!allAnswered || isSubmitting}
          >
            {isSubmitting
              ? (labels?.submitting ?? 'Submitting...')
              : (labels?.submit ?? 'Submit feedback')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
